<template>
  <div
    class="w-full overflow-x-auto rounded-lg border border-lightGray30 bg-white"
    :data-testid="testId"
  >
    <table class="w-full min-w-[980px] border-collapse text-left">
      <thead>
        <tr>
          <th v-if="selectable" class="w-10 border-b border-lightGray30 px-2.5 py-2.5">
            <input
              type="checkbox"
              class="h-4 w-4 cursor-pointer accent-primary"
              aria-label="Selecionar todos"
              :checked="areAllSelected"
              :data-testid="`${testId}-select-all`"
              @change="toggleAll(($event.target as HTMLInputElement).checked)"
            />
          </th>
          <th
            v-for="column in columns"
            :key="column.key"
            class="border-b border-lightGray30 px-2.5 py-2.5 text-[11px] font-bold uppercase tracking-wider text-neutral50"
            :class="[column.widthClass, column.align === 'right' ? 'text-right' : 'text-left']"
            :aria-sort="ariaSortFor(column.key)"
          >
            <button
              v-if="column.sortable"
              type="button"
              class="inline-flex items-center gap-1 uppercase tracking-wider"
              :data-testid="`${testId}-sort-${column.key}`"
              @click="toggleSort(column.key)"
            >
              {{ column.label }}
              <span aria-hidden="true">{{ sortIndicator(column.key) }}</span>
            </button>
            <template v-else>{{ column.label }}</template>
          </th>
          <th
            v-if="$slots.actions"
            class="border-b border-lightGray30 px-2.5 py-2.5 text-[11px] font-bold uppercase tracking-wider text-neutral50"
          >
            Ações
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in rows"
          :key="rowKey(row)"
          class="align-top"
          :data-testid="`${testId}-row-${rowKey(row)}`"
        >
          <td v-if="selectable" class="border-b border-lightGray30 px-2.5 py-2.5">
            <input
              v-if="canSelect(row)"
              type="checkbox"
              class="h-4 w-4 cursor-pointer accent-primary"
              :aria-label="`Selecionar item ${rowKey(row)}`"
              :checked="selectedIds.includes(rowKey(row))"
              :data-testid="`${testId}-select-${rowKey(row)}`"
              @change="toggleOne(rowKey(row), ($event.target as HTMLInputElement).checked)"
            />
          </td>
          <td
            v-for="column in columns"
            :key="column.key"
            class="border-b border-lightGray30 px-2.5 py-2.5 text-sm text-textPrimary"
            :class="column.align === 'right' ? 'text-right' : 'text-left'"
          >
            <slot :name="`cell-${column.key}`" :row="row">{{ column.value?.(row) ?? '' }}</slot>
          </td>
          <td v-if="$slots.actions" class="border-b border-lightGray30 px-2.5 py-2.5">
            <div class="flex flex-wrap gap-1">
              <slot name="actions" :row="row" />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts" generic="T">
import { computed } from 'vue'

import type { QueueColumn, QueueSort } from './types'

const props = withDefaults(
  defineProps<{
    rows: T[]
    columns: QueueColumn<T>[]
    rowKey: (row: T) => number
    selectable?: boolean
    selectedIds?: number[]
    /** Ausente = toda linha é selecionável. */
    isRowSelectable?: (row: T) => boolean
    sort?: QueueSort | null
    testId?: string
  }>(),
  {
    selectable: false,
    selectedIds: () => [],
    isRowSelectable: undefined,
    sort: null,
    testId: 'queue-table'
  }
)

const emit = defineEmits<{
  'update:selectedIds': [ids: number[]]
  'update:sort': [sort: QueueSort]
}>()

const canSelect = (row: T): boolean => props.isRowSelectable?.(row) ?? true

const selectableIds = computed(() => props.rows.filter(canSelect).map(props.rowKey))

const areAllSelected = computed(
  () =>
    selectableIds.value.length > 0 &&
    selectableIds.value.every((id) => props.selectedIds.includes(id))
)

function toggleOne(id: number, checked: boolean): void {
  const next = checked
    ? [...props.selectedIds, id]
    : props.selectedIds.filter((selected) => selected !== id)
  emit('update:selectedIds', next)
}

/**
 * "Todos" é todos DESTA página e selecionáveis. Ids já selecionados fora do
 * recorte visível continuam selecionados: desmarcar o cabeçalho não pode apagar
 * uma seleção que a pessoa fez noutra página.
 */
function toggleAll(checked: boolean): void {
  const visible = selectableIds.value
  const rest = props.selectedIds.filter((id) => !visible.includes(id))
  emit('update:selectedIds', checked ? [...rest, ...visible] : rest)
}

function toggleSort(key: string): void {
  const direction = props.sort?.key === key && props.sort.direction === 'asc' ? 'desc' : 'asc'
  emit('update:sort', { key, direction })
}

const sortIndicator = (key: string): string =>
  props.sort?.key !== key ? '' : props.sort.direction === 'asc' ? '▲' : '▼'

const ariaSortFor = (key: string): 'ascending' | 'descending' | 'none' =>
  props.sort?.key !== key ? 'none' : props.sort.direction === 'asc' ? 'ascending' : 'descending'
</script>
